import { Injectable } from '@angular/core';
import { BaseDownloadServiceWithFilter } from './base.service';
import { Sinvoice } from '@cl/shared/models/sinvoices/sinvoice.model';
import { HttpClient } from '@angular/common/http';
import { NzImageService } from 'ng-zorro-antd/image';
import { lastValueFrom, Observable } from 'rxjs';
import { Client, CommandResult } from '@cl/shared/models';
import { FileMetadata } from '@cl/shared/models/file.model';
import { SinvoiceFilter } from '@cl/shared/models/sinvoices/sinvoice-filter.model';
import { InvoiceClientNotificationConfiguration } from '@cl/shared/models/invoices/invoice-client-notification-configuration.model';

@Injectable({
    providedIn: 'root',
})
export class SinvoicesService extends BaseDownloadServiceWithFilter<Sinvoice, SinvoiceFilter> {
    constructor(
        protected httpClient: HttpClient,
        protected nzImageService: NzImageService,
    ) {
        super(httpClient, '/api/v1/sinvoices/', nzImageService);
    }

    async getInvoiceClient(sinvoiceId: number): Promise<CommandResult<Client>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<Client>>(`${this.baseApiUrl}${sinvoiceId}/client`));
    }

    async sendInvoiceNotification(invoiceNotificationConfig: InvoiceClientNotificationConfiguration): Promise<void> {
        return await lastValueFrom(this.httpClient.post<void>(`${this.baseApiUrl}send-notification`, invoiceNotificationConfig));
    }

    async getInvoiceFileMetadata(invoiceId: number): Promise<CommandResult<FileMetadata>> {
        return lastValueFrom(this.httpClient.get<CommandResult<FileMetadata>>(`${this.baseApiUrl}download/${invoiceId}`));
    }

    async downloadInvoice(invoiceId: number) {
        return this.handleFileDownloading(this.getInvoiceFileMetadata(invoiceId));
    }

    async downloadInvoiceEInvoiceXML(invoiceId: number) {
        return this.handleGeneratedFileDownload(
            lastValueFrom(
                this.httpClient.get<Blob>(`${this.baseApiUrl}download-e-invoice/${invoiceId}`, {
                    responseType: 'blob' as 'json',
                    observe: 'response',
                }),
            ),
        );
    }

    async getInvoiceDetails(id: number): Promise<CommandResult<Sinvoice>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<Sinvoice>>(`${this.baseApiUrl}details/${id}`));
    }

    async addInvoiceToSinvoice(sinvoiceId: number, invoiceId: number): Promise<CommandResult<void>> {
        return await lastValueFrom(this.httpClient.put<CommandResult<void>>(`${this.baseApiUrl}add-invoice/${sinvoiceId}/${invoiceId}`, null));
    }

    async removeInvoiceFromSinvoice(sinvoiceId: number, invoiceId: number): Promise<CommandResult<void>> {
        return await lastValueFrom(this.httpClient.put<CommandResult<void>>(`${this.baseApiUrl}remove-invoice/${sinvoiceId}/${invoiceId}`, null));
    }

    async restoreFromDeleted(sinvoiceId: number): Promise<CommandResult<void>> {
        return await lastValueFrom(this.httpClient.put<CommandResult<void>>(`${this.baseApiUrl}restore-from-deleted/${sinvoiceId}`, null));
    }

    isSinvoiceNumberUnique(number: string, id: number): Observable<boolean> {
        return this.httpClient.get<boolean>(`${this.baseApiUrl}/${number}/number-unique/${id}`);
    }
}
